/** @jsxImportSource theme-ui */

import React, { Fragment } from 'react'

import { Pagination } from '@material-ui/lab'
import moment from 'moment'
import { GetStaticProps } from 'next'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { HeaderHeight } from 'src/components/Header/types'
import SEO from 'src/components/SEO'
import { ShopName } from 'src/constants'
import { Box, Button, Container, Flex, Text } from 'theme-ui'
import { getShopifyArticles } from '~/api/getAllShopifyArticles'
import { withPageProps } from '~/api/services'
import { Styled } from '~/components/styled'
import { articlesPerPage } from '~/pages/blog/page/[page]'

type Props = Awaited<ReturnType<typeof getStaticProps>>['props']

const Item = React.memo(function Item(props: {
  image?: { altText?: string; src: string; width: number; height: number }
  author?: string
  title?: string
  excerptHtml?: string
  publishedAt?: string
  index: number
  handle: string
}) {
  const { image, index, title, excerptHtml, publishedAt, handle } = props
  const imageOnRight = !(index % 2)

  return (
    <Link
      sx={{
        color: 'initial',
        textDecoration: 'none',
        '&:hover img': {
          transform: [null, 'scale(1.01)'],
        },
        '&:hover .bg': {
          backgroundColor: 'background',
        },
      }}
      href={`/blog/${handle}`}
    >
      <Box
        className="bg"
        sx={{
          marginBottom: [7, 9],
          transition: 'background 0.25s ease-in-out',
          bg: 'transparent',
          p: [3, 4],
          borderRadius: [0, 3],
        }}
      >
        <Flex
          sx={{
            display: ['block', 'flex'],
            flexDirection: [null, !imageOnRight ? 'row' : 'row-reverse'],
          }}
        >
          <Box
            sx={{
              marginRight: [0, imageOnRight ? 0 : 5],
              marginLeft: [null, imageOnRight ? 5 : 0],
              flex: [null, 1],
            }}
          >
            {!!image?.src && (
              <Image
                src={image.src}
                height={image.height}
                width={image.width}
                alt={image?.altText || title || 'Blog Image'}
                sx={{
                  aspectRatio: 16/9,
                  borderRadius: 3,
                  boxShadow: 'header',
                  margin: 0,
                  transition: 'transform .25s ease-in-out',
                  transform: [null, 'scale(1)'],
                  objectFit: 'cover',
                  maxWidth: '100%',
                  height: 'auto',
                }}
              />
            )}
          </Box>
          <Box sx={{ flex: [null, 1] }}>
            <Text sx={{ color: 'gray', marginBottom: 3 }}>
              {moment(publishedAt).fromNow()}
            </Text>
            <Styled.h2
              sx={{ marginBottom: 3, fontSize: [null, 6], fontWeight: '600' }}
            >
              {title}
            </Styled.h2>
            <Box dangerouslySetInnerHTML={{ __html: excerptHtml ?? '' }} />
            <Button sx={{ mt: [3, 5] }} variant="pillOutline">
              Continue Reading
            </Button>
          </Box>
        </Flex>
      </Box>
    </Link>
  )
})
export default function BlogPage(props: Props) {
  const { articles, pageNumber, numberOfPages } = props
  const navigate = useRouter().push

  return (
    <Fragment>
      <SEO title={`${ShopName} Blog`} image={articles?.[0]?.image?.src} />
      <Box
        sx={{
          pt: HeaderHeight,
          backgroundImage: (theme) =>
            `linear-gradient(0deg, ${theme.colors?.mutedSecondary}, ${theme.colors?.mutedSecondary})`,
        }}
      >
        <Container
          sx={{
            py: [6, 8],
          }}
        >
          <Styled.h1
            sx={{
              px: 3,
              textAlign: 'center',
              fontSize: [null, 7, 8],
              mb: [7, 8],
              fontWeight: '600',
            }}
          >
            {ShopName} Blog
          </Styled.h1>
          {articles?.map((node, index) => {
            return (
              node?.id && (
                <Item
                  author={node.author}
                  excerptHtml={node.excerptHtml}
                  image={node.image}
                  publishedAt={node.publishedAt}
                  title={node.title}
                  handle={node.handle}
                  key={node.id}
                  index={index}
                />
              )
            )
          })}
          <Pagination
            page={pageNumber}
            count={numberOfPages}
            onChange={(_, page) => {
              if (page > 1) {
                navigate(`/blog/page/${page}`)
              } else {
                navigate('/blog')
              }
            }}
          />
        </Container>
      </Box>
    </Fragment>
  )
}

export const getStaticProps = (async ({ params }) => {
  const page = Number(params?.page ?? 1)

  const articles = await getShopifyArticles()
  const slicedArticles = articles.slice(
    (page - 1) * articlesPerPage,
    page * articlesPerPage
  )

  return {
    props: await withPageProps({
      articles: slicedArticles,
      pageNumber: page,
      numberOfPages: Math.ceil(articles.length / articlesPerPage),
      headerProps: {
        leftTintColor: ['text'],
        rightTintColor: ['text'],
        whiteLogo: [false],
        sticky: false,
      },
    }),
    revalidate: 60*60,
  }
}) satisfies GetStaticProps
